import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import CardHeader from "@mui/material/CardHeader";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useUser } from "../../Context/UserContext";
import { GetApi, PostApi } from "../../Services/api/ApiCall";

const AssignSchemes = () => {
  const { token, authData } = useUser();

  // --- State ---
  const [pcList, setPcList] = useState([]);
  const [schemeList, setSchemeList] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [pcSchemeSelection, setPcSchemeSelection] = useState({});
  const [pcAssignedStatus, setPcAssignedStatus] = useState({});

  // --- Fetch Data on Mount ---
  useEffect(() => {
    const fetchAssignmentData = async () => {
      if (!authData?.user_id || !authData?.role) return;

      setLoadingCenters(true);
      try {
        const centerUrl = `/officers/get-producion-center/${authData.user_id}/${authData.role}`;
        const centerRes = await GetApi(centerUrl, token);
        const schemeRes = await GetApi("/officers/get-all-schemes", token);

        if (centerRes.success && centerRes.data) {
          setPcList(centerRes.data);
          const initialSelection = {};
          const initialStatus = {};
          
          centerRes.data.forEach((pc) => {
            initialSelection[pc.id] = pc.scheme_ids || [];
            initialStatus[pc.id] = pc.scheme_ids && pc.scheme_ids.length > 0;
          });
          
          setPcSchemeSelection(initialSelection);
          setPcAssignedStatus(initialStatus);
        }

        const schemes = schemeRes.results || schemeRes.data || [];
        if (schemes.length > 0) {
          setSchemeList(schemes);
        }
      } catch (err) {
        console.error("Error loading assignment data:", err);
        toast.error("Failed to load assignment data. Please try again.");
      } finally {
        setLoadingCenters(false);
      }
    };

    fetchAssignmentData();
  }, [authData.user_id, authData.role, token]);

  // --- Handlers ---
  const handleSchemeChange = (event, centerId) => {
    const { target: { value } } = event;
    const typeofValue = typeof value;
    const newValue = typeofValue === 'string' ? value.split(',') : value;
    setPcSchemeSelection((prev) => ({ ...prev, [centerId]: newValue }));
  };

  const handleAssignSchemes = async (centerId) => {
    const selectedSchemes = pcSchemeSelection[centerId] || [];
    if (selectedSchemes.length === 0) {
      toast.warning("Please select at least one scheme before assigning.");
      return;
    }
    
    try {
      await PostApi("/officers/assign-schemes", { center_id: centerId, scheme_ids: selectedSchemes }, token);
      setPcAssignedStatus((prev) => ({ ...prev, [centerId]: true }));
      toast.success("Schemes assigned successfully!");
    } catch (error) {
      console.error("Assignment failed", error);
      toast.error("Failed to assign schemes. Please try again.");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: "#f9fafb", minHeight: "80vh" }}>
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      {/* Page Header */}
      <Box mb={4}>
        <Typography variant="h4" sx={{ color: "#1b5e20", fontWeight: 800, mb: 1 }}>
        Manage Production Center Schemes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Assign specific schemes to private production centers under your jurisdiction.
        </Typography>
      </Box>

      {/* Content Grid */}
      {loadingCenters ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
          <CircularProgress size={50} />
        </Box>
      ) : pcList.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h6" color="text.secondary">No Production Centers Found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            There are no private production centers available to assign schemes.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {pcList.map((center) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={center.id}>
              <Card 
                elevation={3} 
                sx={{   
                  height: "100%", 
                  display: "flex", 
                  flexDirection: "column", 
                  borderRadius: 3, 
                  maxWidth: 450,
                  width: "100%",
                  transition: "0.3s", 
                  "&:hover": { boxShadow: 8, transform: "translateY(-4px)" } 
                }}
              >
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: "#1b5e20", width: 48, height: 48 }} variant="rounded">
                      {center.name_of_production_centre?.charAt(0) || "PC"}
                    </Avatar>
                  }
                  title={<Typography fontWeight={700} variant="subtitle1" noWrap>{center.name_of_production_centre}</Typography>}
                  subheader={<Typography variant="caption" color="text.secondary">{center.production_center_code || "No Code"}</Typography>}
                />

                <CardContent sx={{ flexGrow: 1 }}>
                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Address:</Typography>
                    <Typography variant="body2" sx={{ fontSize: "13px", lineHeight: 1.4 }}>{center.complete_address}</Typography>
                  </Box>

                  {/* Dropdown showing Scheme Names as Chips */}
                  <FormControl fullWidth size="small">
                    <InputLabel id={`scheme-label-${center.id}`}>Select Schemes</InputLabel>
                    <Select
                      labelId={`scheme-label-${center.id}`}
                      multiple
                      value={pcSchemeSelection[center.id] || []}
                      onChange={(e) => handleSchemeChange(e, center.id)}
                      input={<OutlinedInput label="Select Schemes" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const scheme = schemeList.find((s) => s.id === value);
                            return (
                              <Chip 
                                key={value} 
                                label={scheme ? scheme.name : "Unknown"} 
                                size="small" 
                                sx={{ height: 22, fontSize: "0.75rem" }} 
                              />
                            );
                          })}
                        </Box>
                      )}
                      MenuProps={{ 
                        PaperProps: { 
                          style: { maxHeight: 250, width: 300 }
                        } 
                      }}
                    >
                      {schemeList.map((scheme) => (
                        <MenuItem key={scheme.id} value={scheme.id}>
                          <Checkbox checked={((pcSchemeSelection[center.id] || []).indexOf(scheme.id) > -1)} />
                          <ListItemText primary={scheme.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0, justifyContent: "flex-end" }}>
                  <Button
                    variant={pcAssignedStatus[center.id] ? "outlined" : "contained"}
                    size="small"
                    onClick={() => handleAssignSchemes(center.id)}
                    startIcon={pcAssignedStatus[center.id] ? <EditIcon /> : <SaveIcon />}
                    sx={{ 
                      fontWeight: 600, 
                      color: pcAssignedStatus[center.id] ? "#1b5e20" : "#fff", 
                      borderColor: "#1b5e20",
                      "&:hover": {
                        borderColor: "#004d16",
                        bgcolor: pcAssignedStatus[center.id] ? "rgba(27, 94, 32, 0.04)" : "#004d16"
                      }
                    }}
                  >
                    {pcAssignedStatus[center.id] ? "Edit Schemes" : "Assign Schemes"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default AssignSchemes;